import { CardGrid } from "../../../components/CardGrid.tsx";
import { Card } from "../../../components/Card.tsx";
import { FieldDisplay } from "../../../components/FieldDisplay.tsx";
import { PropertyContactDisplay } from "../../../components/PropertyContactDisplay.tsx";
import { OtherContactDisplay } from "../../../components/OtherContactDisplay.tsx";
import {
    ClipboardList,
    DollarSign,
    Landmark,
    MapPin,
    Calendar,
    Home,
    FileText,
    Briefcase,
    HashIcon,
    Building2,
    User
} from "lucide-react";
import { formatCurrency, formatDate } from "../../../utils/dateUtils.ts";
import { GoogleMap } from "../../../components/google/GoogleMap.tsx";
import React from "react";
import { HtmlContent } from "../../../components/HtmlConent.tsx";
import {FullOrderDetails, ReportType} from "../../../api/ApiTypes.ts";

export interface StaffOrderDetailsProps {
    orderDetails?: FullOrderDetails;
}


export const StaffOrderDetailsTab: React.FC<StaffOrderDetailsProps> = ({ orderDetails }) => {
    if (!orderDetails) {
        return (
            <div className="bg-white rounded-lg p-6 text-center border border-slate-200">
                <p className="text-slate-500">Order details are not available.</p>
            </div>
        );
    }

    // Extract report types from the Map structure
    const reportTypes: (ReportType | undefined)[] = [
        orderDetails.appraisal.reportType1 ? (orderDetails.reportTypes as any)?.[orderDetails.appraisal.reportType1] : undefined,
        orderDetails.appraisal.reportType2 ? (orderDetails.reportTypes as any)?.[orderDetails.appraisal.reportType2] : undefined,
        orderDetails.appraisal.reportType3 ? (orderDetails.reportTypes as any)?.[orderDetails.appraisal.reportType3] : undefined,
        orderDetails.appraisal.reportType4 ? (orderDetails.reportTypes as any)?.[orderDetails.appraisal.reportType4] : undefined,
    ].filter(Boolean);


    // Build full property address
    const propertyAddress = [
        orderDetails.appraisal.propertyAddressLine1,
        orderDetails.appraisal.propertyAddressLine2
    ].filter(Boolean).join(', ');


    // Build ordered by address
    const orderedByAddress = [
        orderDetails.orderedBy.officeAddressLine,
        orderDetails.orderedBy.officeCity,
        orderDetails.orderedBy.officeStateCode,
        orderDetails.orderedBy.officeZipCode
    ].filter(Boolean).join(', ');

    // Extract property contact and other contacts
    const propertyContact = orderDetails.orderContacts?.find(c => c.isPropertyContact);
    const otherContacts = orderDetails.orderContacts?.filter(c => !c.isPropertyContact) || [];

    return (
        <div>

            <CardGrid>
                {/* Property Information Section */}
                <Card title="Property Information" colSpan={2}>
                    <FieldDisplay
                        label="Property Address"
                        value={
                            <>
                                <p className="text-slate-700">{propertyAddress}</p>
                                {orderDetails.appraisal.propertyCounty && (
                                    <p className="text-sm text-slate-500">
                                        County: {orderDetails.appraisal.propertyCounty}
                                    </p>
                                )}
                            </>
                        }
                        icon={<MapPin className="h-5 w-5 text-blue-500" />}
                        className="mb-4"
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                        <FieldDisplay
                            label="Property Type"
                            value={orderDetails.propertyType?.description}
                            icon={<Home className="h-5 w-5 text-blue-500" />}
                        />

                        <FieldDisplay
                            label="Intended Use"
                            value={orderDetails.intendedUse?.description}
                            icon={<Briefcase className="h-5 w-5 text-blue-500" />}
                        />

                        <FieldDisplay
                            label="Purchase Price"
                            value={formatCurrency(orderDetails.appraisal.purchasePrice)}
                            icon={<DollarSign className="h-5 w-5 text-blue-500" />}
                        />

                        {orderDetails.appraisal.estimatedValue && (
                            <FieldDisplay
                                label="Estimated Value"
                                value={formatCurrency(orderDetails.appraisal.estimatedValue)}
                                icon={<DollarSign className="h-5 w-5 text-blue-500" />}
                            />
                        )}

                        {orderDetails.appraisal.loanAmount && (
                            <FieldDisplay
                                label="Loan Amount"
                                value={formatCurrency(orderDetails.appraisal.loanAmount)}
                                icon={<DollarSign className="h-5 w-5 text-blue-500" />}
                            />
                        )}

                        {orderDetails.appraisal.apn && (
                            <FieldDisplay
                                label="APN"
                                value={orderDetails.appraisal.apn}
                                icon={<FileText className="h-5 w-5 text-blue-500" />}
                            />
                        )}
                    </div>
                </Card>

                {/* Financial & Dates Section */}
                <Card title="Financial Information">
                    <FieldDisplay
                        label="Vendor Fee"
                        value={formatCurrency(orderDetails.appraisal.appraiserFee1)}
                        icon={<DollarSign className="h-5 w-5 text-blue-500" />}
                        className="mb-4"
                    />
                    {Number(orderDetails?.appraisal.technologyFee) > 0 && (
                        <FieldDisplay
                            label="Technology Fee"
                            value={formatCurrency(orderDetails.appraisal.technologyFee)}
                            icon={<DollarSign className="h-5 w-5 text-blue-500" />}
                            className="mb-4"
                        />
                    )}

                    <FieldDisplay
                        label="Client Balance"
                        value={formatCurrency(orderDetails.appraisal.clientBalance)}
                        icon={<DollarSign className="h-5 w-5 text-blue-500" />}
                    />
                </Card>

                {/* Dates & Reference Numbers */}
                <Card title="Dates & References" colSpan={2}>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <FieldDisplay
                            label="Inspection Date"
                            value={formatDate(orderDetails.appraisal.inspectionActualDate)}
                            icon={<Calendar className="h-5 w-5 text-blue-500" />}
                        />

                        {orderDetails?.appraisal.estimatedClosingDate && (
                            <FieldDisplay
                                label="Estimated Closing Date"
                                value={formatDate(orderDetails.appraisal.estimatedClosingDate)}
                                icon={<Calendar className="h-5 w-5 text-blue-500" />}
                            />
                        )}

                        <FieldDisplay
                            label="Estimated Delivery Date"
                            value={formatDate(orderDetails.appraisal.estimatedDeliveryDate)}
                            icon={<Calendar className="h-5 w-5 text-blue-500" />}
                        />

                        <FieldDisplay
                            label="Loan Number"
                            value={orderDetails.appraisal.loanId}
                            icon={<HashIcon className="h-5 w-5 text-blue-500" />}
                        />

                        {orderDetails.appraisal.fhaCaseId && (
                            <FieldDisplay
                                label="FHA Case ID"
                                value={orderDetails.appraisal.fhaCaseId}
                                icon={<FileText className="h-5 w-5 text-blue-500" />}
                            />
                        )}

                        <FieldDisplay
                            label="USA File Number"
                            value={orderDetails.appraisal.usaFileNumber}
                            icon={<FileText className="h-5 w-5 text-blue-500" />}
                        />
                    </div>
                </Card>

                {/* Report Types Section */}
                <Card title="Report Types">
                    <div className="divide-y divide-slate-200">
                        {reportTypes.map((report, index) => (
                            <div key={index} className={index > 0 ? 'pt-2 mt-2' : ''}>
                                <h3 className="font-medium text-slate-900">
                                    {report?.productDescription}
                                </h3>
                            </div>
                        ))}
                    </div>
                </Card>

                {/* Client Info */}
                <Card title="Client Information">
                    <FieldDisplay
                        label="Ordered By"
                        value={
                            <>
                                <h3 className="font-medium text-slate-900">
                                    {orderDetails.orderedBy.companyName}
                                </h3>
                                <p className="text-slate-700 text-sm mt-1">{orderedByAddress}</p>
                                <p className="text-slate-600 text-sm">
                                    Contact: {orderDetails.orderedBy.mainContactName}
                                </p>
                                <p className="text-slate-600 text-sm">
                                    Email: {orderDetails.orderedBy.companyEmailAddress}
                                </p>
                            </>
                        }
                        icon={<Landmark className="h-5 w-5 text-blue-500" />}
                    />
                </Card>

                {/* Staff Company Information (if available) */}
                {orderDetails.staffCompany && (
                    <Card title="Staff Company">
                        <FieldDisplay
                            label="Company"
                            value={
                                <>
                                    <h3 className="font-medium text-slate-900">
                                        {orderDetails.staffCompany.companyName}
                                    </h3>
                                    {orderDetails.staffCompanyLicense && (
                                        <p className="text-slate-600 text-sm mt-1">
                                            License: {orderDetails.staffCompanyLicense.license}
                                            ({orderDetails.staffCompanyLicense.stateCode})
                                        </p>
                                    )}
                                </>
                            }
                            icon={<Building2 className="h-5 w-5 text-blue-500" />}
                        />
                    </Card>
                )}

                {/* Requested By Information */}
                <Card title="Requested By">
                    <FieldDisplay
                        label="User"
                        value={
                            <>
                                <h3 className="font-medium text-slate-900">
                                    {orderDetails.requestedBy?.securityUser?.firstName} {orderDetails.requestedBy?.securityUser?.lastName}
                                </h3>
                                <p className="text-slate-600 text-sm">
                                    Email: {orderDetails.requestedBy?.securityUser?.email}
                                </p>
                                <p className="text-slate-600 text-sm">
                                    Phone: {orderDetails.requestedBy?.securityUser?.workPhone}
                                </p>
                            </>
                        }
                        icon={<User className="h-5 w-5 text-blue-500" />}
                    />
                </Card>

                {/* Contact Information */}
                <Card title="Contact Information" colSpan={2}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                        {propertyContact && (
                            <PropertyContactDisplay contact={propertyContact} />
                        )}

                        { otherContacts.length > 0 && (
                            <div className="mb-4">
                                <div className="p-4 bg-white rounded-md border border-slate-200">
                                    {otherContacts.map((contact) => (
                                        <OtherContactDisplay key={contact.id} contact={contact} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {!propertyContact && otherContacts.length === 0 && (
                        <p className="text-slate-500 italic">No contacts available</p>
                    )}
                </Card>

                {/* Notes Section */}
                <Card title="Notes & Instructions" colSpan={3}>
                    <div className="flex items-start">
                        <ClipboardList className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                        <div className="prose prose-sm max-w-none">
                            <HtmlContent
                                content={orderDetails.appraisal.notesAndSpecialInstructions}
                                className="font-sans text-slate-700"
                                emptyMessage="No notes or instructions available"
                            />
                        </div>
                    </div>
                </Card>

                {/* Property Maps Section */}
                <Card title={propertyAddress} colSpan={3}>
                    <GoogleMap
                        lat={orderDetails.appraisal.propertyLatitude}
                        lng={orderDetails.appraisal.propertyLongitude}
                    />
                </Card>
            </CardGrid>
        </div>
    );
};